/**
 * Сводка «за неделю» для тех, кто ведёт данные: сколько замечаний
 * появилось, сколько исправлено и отмечено обработанными — в зоне
 * ответственности, по дивизионам, и что именно появилось.
 *
 * Данные — от сервера: он раз в 10 минут записывает, когда замечание
 * появилось и когда исчезло (таблица switch_issues).
 */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { api } from '../../api/client';
import { selectPrefixMap } from '../../store/slices/switchesSlice';
import { objectLabel } from '../../domain/issues';
import { agoText, trackingNote } from '../../domain/weekSummary';
import styles from './WeekSummary.module.css';

const REFRESH_MS = 10 * 60 * 1000;
const RECENT_SHOWN = 6;

export default function WeekSummary() {
  const navigate = useNavigate();
  const prefixMap = useSelector(selectPrefixMap);
  const [summary, setSummary] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get('/api/dashboard/week')
        .then((s) => {
          if (alive) {
            setSummary(s);
            setFailed(false);
          }
        })
        .catch(() => {
          if (alive) setFailed(true);
        });
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  if (failed && !summary) return null; // сводка — дополнение, без неё дашборд работает
  if (!summary) {
    return (
      <section className={styles.card} aria-busy="true">
        <h2 className={styles.title}>За неделю</h2>
      </section>
    );
  }

  const { totals, divisions, recent } = summary;
  const note = trackingNote(summary);

  return (
    <section className={styles.card} aria-labelledby="week-title">
      <div className={styles.head}>
        <h2 id="week-title" className={styles.title}>
          За неделю
        </h2>
        <span className={styles.scope}>
          {summary.scope === 'all'
            ? 'вся сеть'
            : divisions.length === 1
              ? divisions[0].name
              : 'ваши дивизионы'}
        </span>
      </div>

      <dl className={styles.totals}>
        <div className={styles.total}>
          <dt>Новые замечания</dt>
          <dd className={totals.added > 0 ? styles.added : undefined}>
            {totals.added > 0 ? `+${totals.added}` : 0}
          </dd>
        </div>
        <div className={styles.total}>
          <dt>Исправлено</dt>
          <dd className={totals.fixed > 0 ? styles.fixed : undefined}>{totals.fixed}</dd>
        </div>
        <div className={styles.total}>
          <dt>Отмечено обработанными</dt>
          <dd>{totals.processed}</dd>
        </div>
      </dl>

      {divisions.length > 1 && (
        <table className={styles.divs}>
          <thead>
            <tr>
              <th>Дивизион</th>
              <th title="Новые замечания">Новые</th>
              <th>Испр.</th>
              <th title="Отмечено обработанными">Обраб.</th>
            </tr>
          </thead>
          <tbody>
            {divisions.map((d) => (
              <tr key={d.name}>
                <td>{d.name}</td>
                <td className={d.added > 0 ? styles.added : undefined}>
                  {d.added > 0 ? `+${d.added}` : '—'}
                </td>
                <td>{d.fixed || '—'}</td>
                <td>{d.processed || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className={styles.subtitle}>Что появилось</h3>
      {recent.length === 0 ? (
        <p className={styles.quiet}>За неделю новых замечаний нет.</p>
      ) : (
        <ul className={styles.recent}>
          {recent.slice(0, RECENT_SHOWN).map((r) => (
            <li key={`${r.mag}|${r.sw}|${r.issue}|${r.at}`}>
              <button
                type="button"
                className={styles.item}
                onClick={() => navigate(`/objects/${r.mag}/${r.sw}`)}
              >
                <span className={styles.itemTop}>
                  <span className={styles.itemName}>
                    {objectLabel(r.mag, prefixMap)} · {r.sw}
                  </span>
                  <span className={styles.itemAgo}>{agoText(r.at)}</span>
                </span>
                <span className={`${styles.itemIssue} ${r.fixed ? styles.itemFixed : ''}`}>
                  {r.issue}
                  {r.fixed && ' — уже исправлено'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {recent.length > RECENT_SHOWN && (
        <p className={styles.quiet}>
          И ещё {recent.length - RECENT_SHOWN} — в «Проблемных записях».
        </p>
      )}

      {note && <p className={styles.note}>{note}</p>}
    </section>
  );
}
